import React, { useEffect, useState } from "react";
import { Card, CardActionArea, CardContent, CardMedia, Typography, Tooltip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom"; // Import useNavigate hook
import AspectRatioOutlinedIcon from "@mui/icons-material/AspectRatioOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import "./MachineCard.css";
import Swal from "sweetalert2";
import customHttp from "../../services/customHTTP";
import ModelButton from "../ModelButton/ModelButton";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";

const MachineCard = ({ product }) => {
	const navigate = useNavigate(); // Hook to navigate to other pages
	const [has3DModel, setHas3DModel] = useState(false);
	const [has3DModelCP, setHas3DModelCP] = useState(false);
	const [has2DModel, setHas2DModel] = useState(false);
	const [hasMultiple3DModels, setHasMultiple3DModels] = useState(false);

	const default_img = `${ASSETS_BASE_URL}/stock.png`;
	const productImageUrl = `${ASSETS_BASE_URL}/MachineImages/${product.machine_number}.png`;
	const product3DModelUrl = `${SERVER_BASE_URL}/stock-models/3D/${product.machine_number}.html`;
	const product3DModelUrlCP = `${SERVER_BASE_URL}/stock-models/3D/${product.machine_number}_CP.html`;
	const product2DModelUrl = `${SERVER_BASE_URL}/stock-models/2D/${product.machine_number}.pdf`;

	// useEffect(() => {
	// 	const checkResources = async () => {
	// 		try {
	// 			// Check if the 3D model exists
	// 			const modelResponse = await fetch(product3DModelUrl, { method: "HEAD" });
	// 			const modelResponseCP = await fetch(product3DModelUrlCP, { method: "HEAD" });
	// 			if (modelResponse.ok && modelResponseCP.ok) {
	// 				setHas3DModel(true);
	// 				setHas2DModel(false);
	// 				setHas3DModelCP(true);
	// 			} else if (modelResponse.ok) {
	// 				setHas3DModel(true);
	// 				setHas2DModel(false);
	// 				setHas3DModelCP(false);
	// 			} else if (modelResponseCP.ok) {
	// 				setHas3DModel(false);
	// 				setHas2DModel(false);
	// 				setHas3DModelCP(true);
	// 			} else {
	// 				const model2dResponse = await fetch(product2DModelUrl, { method: "HEAD" });
	// 				if (model2dResponse.ok) {
	// 					setHas2DModel(true);
	// 					setHas3DModel(false);
	// 				} else {
	// 					setHas2DModel(false);
	// 					setHas3DModel(false);
	// 				}
	// 			}
	// 		} catch (error) {
	// 			console.warn("Resource check failed. Using defaults.");
	// 		}
	// 	};

	// 	checkResources();
	// }, [product.machine_number]);

	useEffect(() => {
		const checkResources = async () => {
			try {
				const base3DUrl = `${SERVER_BASE_URL}/stock-models/3D/${product.machine_number}.html`;
				const changePartUrl = `${SERVER_BASE_URL}/stock-models/3D/${product.machine_number}_CP.html`;

				// Step 1: Check for base model and change part
				const [resBase, resCP] = await Promise.all([
					fetch(base3DUrl, { method: "HEAD" }),
					fetch(changePartUrl, { method: "HEAD" }),
				]);

				let found3D = resBase.ok;
				let foundCP = resCP.ok;
				let multiple3D = false;

				// Step 2: If base model not found, check for multiple numbered models
				if (!found3D) {
					let index = 1;
					let modelCount = 0;

					while (true) {
						const suffix = index.toString().padStart(2, "0");
						const modelUrl = `${SERVER_BASE_URL}/stock-models/3D/${product.machine_number}_${suffix}.html`;

						const res = await fetch(modelUrl, { method: "HEAD" });

						if (res.ok) {
							modelCount++;
							index++;
						} else {
							break;
						}
					}

					if (modelCount > 0) {
						found3D = true;
						multiple3D = modelCount > 1;
					}
				}

				setHas3DModel(found3D);
				setHas3DModelCP(foundCP);
				setHasMultiple3DModels(multiple3D);

				// Step 3: Fallback to 2D
				if (!found3D && !foundCP) {
					const model2dResponse = await fetch(
						`${SERVER_BASE_URL}/stock-models/2D/${product.machine_number}.pdf`,
						{ method: "HEAD" }
					);
					setHas2DModel(model2dResponse.ok);
				} else {
					setHas2DModel(false);
				}
			} catch (error) {
				console.warn("Resource check failed. Using defaults.");
				setHas2DModel(false);
				setHas3DModel(false);
				setHas3DModelCP(false);
				setHasMultiple3DModels(false);
			}
		};

		checkResources();
	}, [product.machine_number]);

	const handleCardClick = async () => {
		if (product.machine_id) {
			// navigate(`/machines/${product.MachineID}`);
			try {
				const reqBody = {
					machine_id: product.machine_id,
				};

				const response = await customHttp.post("/products/bymachineid", reqBody);
				if (response.data.success) {
					const productsCount = response.data.data.length > 0 ? true : false;
					if (productsCount) {
						navigate(`/machines/${product.machine_id}`);
					} else {
						Swal.fire({
							icon: "warning",
							title: "Warning!",
							text: `No Products available for Machine`,
						});
					}
				} else {
					Swal.fire({
						icon: "warning",
						title: "Warning!",
						text: `No Products available for Machine`,
					});
				}
			} catch (err) {
				Swal.fire({
					icon: "error",
					title: "Error!",
					text: `Failed to fetch data.`,
				});
			}
		} else {
			Swal.fire({
				icon: "warning",
				title: "Warning!",
				text: `Machine Not Found`,
			});
		}
	};

	return (
		<Card className="machine-card">
			<CardActionArea className="machine-card-content" onClick={handleCardClick}>
				{" "}
				{/* Added onClick */}
				<CardMedia
					component="img"
					image={productImageUrl} // Use the random image here
					alt={product.machine_name}
					className="machine-img"
					onError={(e) => {
						e.target.src = default_img;
					}}
				/>
				<CardContent sx={{ paddingBottom: "10px", textAlign: "center" }}>
					<Tooltip title={product.machine_name}>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "bold",
								lineHeight: "1.2rem",
								marginBottom: "5px",
								display: "-webkit-box",
								overflow: "hidden",
								WebkitLineClamp: 2,
								WebkitBoxOrient: "vertical",
								textOverflow: "ellipsis",
								minHeight: "2.4rem",
								color: "#000", // Adjust based on the line height to ensure 2 lines of space
							}}>
							{product.machine_name}
						</Typography>
					</Tooltip>

					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
						<b>Machine Serial #:</b>{" "}
						{product.machine_product_name && product.machine_product_name !== "EMPTY"
							? product.machine_product_name
							: "N/A"}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ fontSize: "11px" }}>
						<b>Machine #:</b> {product.machine_number}
					</Typography>

					<div className="action-buttons">
						{has3DModel && (
							<ModelButton
								machineName={product.machine_name}
								machineNumber={product.machine_number}
								machineProductName={product.machine_product_name}
								machineid={product.machine_id}
								changeParts={has3DModelCP}
								hasMultiple3DModels={hasMultiple3DModels}
								onClick={(e) => {
									e.stopPropagation();
								}}
							/>
						)}
						{has2DModel && (
							<Tooltip title="View 2D Model">
								<IconButton
									aria-label="View 2D Model"
									size="large"
									onClick={(e) => {
										e.stopPropagation();
										window.open(product2DModelUrl);
									}}>
									<PictureAsPdfIcon />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title="View Documentation">
							<IconButton
								aria-label="Documentation"
								size="large"
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/documents/${product.machine_id}`);
								}}>
								<DescriptionOutlinedIcon />
							</IconButton>
						</Tooltip>
					</div>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export default MachineCard;
